import clsx from 'clsx';
import type { ReactNode } from 'react';

import { SmallSelectField, type Option } from './SelectField';

type OptionInput = number | Option<number>;

type PerPageSelectProps = {
  value?: number;
  onChange: (value: number) => void;
  options?: OptionInput[];
  label?: ReactNode;
  className?: string;
  selectClassName?: string;
  isSearchable?: boolean;
  transparent?: boolean;
};

const DEFAULT_OPTIONS = [20, 50, 100];

const normalizeOption = (option: OptionInput): Option<number> => {
  if (typeof option === 'number') {
    return {
      label: String(option),
      value: option,
    };
  }

  return {
    label:
      typeof option.label === 'number' ? String(option.label) : option.label,
    value: option.value,
  };
};

export const PerPageSelect = ({
  value,
  onChange,
  options = DEFAULT_OPTIONS,
  label,
  className,
  selectClassName,
  isSearchable = false,
  transparent = true,
}: PerPageSelectProps) => {
  const selectOptions: Option<number>[] = options.map(normalizeOption);
  const selectedOption =
    value === undefined
      ? null
      : (selectOptions.find((opt) => opt.value === value) ?? null);
  const transparentProps = transparent ? { transparent: true as const } : {};

  return (
    <div className={clsx('flex items-center gap-2', className)}>
      {label && (
        <label className="text-sm font-medium text-gray-700">{label}</label>
      )}
      <SmallSelectField<Option<number>, false>
        className={selectClassName ?? 'text-black'}
        options={selectOptions}
        value={selectedOption}
        isSearchable={isSearchable}
        onChange={(newValue) => {
          if (!newValue) return;
          onChange(newValue.value);
        }}
        {...transparentProps}
      />
    </div>
  );
};
